import type { AjaxResponse } from 'rxjs';
import { get } from '@helpcenter/ajax';
import { Observable } from '@helpcenter/epics-rxjs';
import { getHelpCenterAri, getMeta } from '@helpcenter/meta';
import * as qs from 'query-string';
import type { AjaxError } from 'rxjs/observable/dom/AjaxObservable';
// eslint-disable-next-line no-restricted-imports
import { ConfluencePageAri, JiraProjectAri } from '@atlassian/ari';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { trackError } from '@atlassian/help-center-common-util/analytics';
import { APOLLO_STARGATE_CONTEXT_ROOT } from '../constants';

/**
 * Feature flag: kb_article_metadata_help_center_project_resolution
 *
 * When enabled, article-metadata uses helpCenterAri to resolve the project for portal-less
 * article URLs when projectAri is omitted.
 */
const KB_ARTICLE_METADATA_HELP_CENTER_PROJECT_RESOLUTION =
	'kb_article_metadata_help_center_project_resolution';

interface FetchCrossSiteArticleParams {
	articleId: string;
	projectId: number | undefined;
	crossSiteTenantId: string;
}

export interface FetchCrossSiteArticleResponse {
	pageAri: string;
	title: string;
	space: {
		id: string;
		key: string;
	};
	projects: [
		{
			projectId: string;
			projectAri: string;
			portalId: string;
		},
	];
	hostname: string;
}

interface FetchCrossSiteArticleAjaxResponse extends AjaxResponse {
	response: FetchCrossSiteArticleResponse;
}

export const fetchCrossSiteArticles = ({
	articleId,
	projectId,
	crossSiteTenantId,
}: FetchCrossSiteArticleParams): Observable<FetchCrossSiteArticleResponse> => {
	const cloudId = getMeta('tenant-id') || '';
	const activationId = getMeta('activation-id') as string;
	const pageAri = ConfluencePageAri.create({
		siteId: crossSiteTenantId,
		pageId: articleId,
	}).toString();
	const useHelpCenterProjectResolution = FeatureGates.checkGate(
		KB_ARTICLE_METADATA_HELP_CENTER_PROJECT_RESOLUTION,
	);
	const projectAri = projectId
		? JiraProjectAri.create({ projectId: projectId.toString(), siteId: cloudId }).toString()
		: undefined;

	const queryStringV2 = qs.stringify({
		projectAri,
		articleAri: pageAri,
		...(useHelpCenterProjectResolution ? { helpCenterAri: getHelpCenterAri() } : {}),
	});

	const uri = `${APOLLO_STARGATE_CONTEXT_ROOT}/${activationId}/api/jsm-portal/article-metadata?${queryStringV2}`;

	// eslint-disable-next-line @typescript-eslint/no-unsafe-return
	return get(uri, { 'Content-Type': 'application/json' })
		.map(({ response }: FetchCrossSiteArticleAjaxResponse) => response)
		.catch((e: AjaxError) => {
			trackError('categories.fetch.all.failed', {}, e);
			return Observable.throw(e);
		});
};
